import cx from 'classnames';
import {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useGetCategories} from '~/common/components/MegaNav/MegaNavVariant/hooks/useGetCategories';
import type {TabsEnum} from '~/common/components/MegaNav/MegaNavVariant/types';
import {selectIsProOrPartyPass} from '~/common/fabric/User/reducers/user';
import {Button} from '~library/atoms/Button';
import {useTracking} from '~library/context/tracking/tracking-context';

import * as styles from './NavTabs.module.scss';

type NavTabsProps = {
  activeTab: TabsEnum;
  onTabClick: (tab: TabsEnum) => void;
};

export const NavTabs = ({activeTab, onTabClick}: NavTabsProps) => {
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const {megaNavData} = useGetCategories();
  const {track} = useTracking();
  const activeButtonRef = useRef<HTMLButtonElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeTab is needed to trigger scroll on tab change
  useEffect(() => {
    if (activeButtonRef.current) {
      activeButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeTab]);

  const handleTabClick = useCallback(
    (tab: TabsEnum, label: string) => {
      track({
        dataQaId: `mobile-category-drawer-tab-${tab}`,
        label: label,
      });
      onTabClick(tab);
    },
    [onTabClick, track]
  );

  // Get navigation items from globalNav, filtering out Evite Pro if user has Pro/PartyPass
  const navTabs =
    megaNavData?.globalNav
      ?.filter((nav) => {
        // Hide Evite Pro tab if user already has Pro/PartyPass
        if (nav.id === 'evite-pro' && isProOrPartyPass) {
          return false;
        }
        return true;
      })
      .map((nav) => ({
        key: nav.id as TabsEnum,
        label: nav.title,
      })) ?? [];

  return (
    <div className={styles.container}>
      {navTabs.map((tab) => (
        <Button
          {...(activeTab === tab.key && {ref: activeButtonRef})}
          key={tab.key}
          variant="transparent"
          className={cx(styles.tab, {
            [styles.active]: activeTab === tab.key,
          })}
          onClick={() => handleTabClick(tab.key as TabsEnum, tab.label)}
          data-qa-id={`mobile-category-drawer-tab-${tab.key}`}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
};
